/* AML / KYC Notice — explains why we collect identity and beneficial-
   ownership data at signup. Required to comply with the USA PATRIOT
   Act §326 (CIP rule) and FinCEN's beneficial ownership rule. Also
   addresses OFAC screening and SAR filing in plain English. */
import { Link } from "react-router-dom";
import { ShieldAlert, ArrowLeft } from "lucide-react";

export default function AmlKyc() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-aml-kyc">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-xs text-foreground/55 hover:text-foreground inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft className="w-3 h-3"/>Back to verqo
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-foreground text-accent grid place-items-center">
            <ShieldAlert className="w-5 h-5"/>
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Legal · verqo Wholesale OS</div>
        </div>
        <h1 className="display text-4xl tracking-tight mt-2">AML &amp; Identity Verification Notice</h1>
        <div className="text-[12px] text-foreground/55 mt-2">v1.0 · Effective May 1, 2026</div>

        <div className="mt-8 prose prose-sm max-w-none text-foreground/85 leading-relaxed">
          <p>
            <strong>Important federal disclosure:</strong> To help the
            government fight the funding of terrorism and money-laundering
            activities, federal law requires all financial institutions to
            obtain, verify, and record information that identifies each
            person who opens an account. This is the "Customer Identification
            Program" (CIP) rule under USA PATRIOT Act §326.
          </p>

          <h2 className="display text-xl mt-8 mb-2">1. What we collect &amp; why</h2>
          <ul>
            <li><strong>Business KYB</strong> — legal name, EIN, address, business type, year founded, beneficial ownership. Verified via Middesk against state and federal registries.</li>
            <li><strong>Beneficial owner KYC</strong> — legal name, date of birth, last-4 of SSN, identity-verification consent. Required for any individual owning ≥25 % of the entity (per FinCEN's Beneficial Ownership Rule).</li>
            <li><strong>Banking signals</strong> — read-only cash-flow and identity verification via Plaid.</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">2. Sanctions &amp; OFAC screening</h2>
          <p>
            Every business and beneficial owner is screened against the U.S.
            Treasury Office of Foreign Assets Control (OFAC) Specially
            Designated Nationals (SDN) list, FinCEN's 311 list, and other
            applicable sanctions lists. Matches will result in account
            rejection or freeze and may be reported to authorities as required
            by law.
          </p>

          <h2 className="display text-xl mt-8 mb-2">3. Suspicious activity</h2>
          <p>
            verqo and its banking partners are required to monitor account
            activity for unusual or suspicious patterns and may file
            Suspicious Activity Reports (SARs) with FinCEN as required. By law,
            we are <strong>prohibited</strong> from telling you whether a SAR has been filed
            on your account.
          </p>

          <h2 className="display text-xl mt-8 mb-2">4. Data security &amp; retention</h2>
          <ul>
            <li>SSN-last-4 is bcrypt-hashed at rest and never round-trips.</li>
            <li>KYB / KYC records are retained for 5 years after account closure (federal AML retention minimum).</li>
            <li>Access is logged and limited to authorised compliance staff.</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">5. Your rights</h2>
          <p>
            You may request a summary of the identity information we hold on
            you under the <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.
            Note that under federal AML rules, we may not delete that
            information for the duration of the regulatory retention window
            (typically 5 years post-account-closure).
          </p>

          <h2 className="display text-xl mt-8 mb-2">6. Questions</h2>
          <p>
            Compliance team: <a href="mailto:compliance@verqo.com" className="underline">compliance@verqo.com</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
